import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft, Plus, Trash2, RefreshCw, Users, Mail, Clock } from 'lucide-react'
import clsx from 'clsx'
import axios from 'axios'
import { buildApiUrl } from '../../../config/api'
import { toast } from '../../../components/Toast'
import { Button, Badge, Card, EmptyState } from '../../../components/ui'
import { formatDateTime, formatRelativeDate } from '../../../utils/dateUtils'
import InviteUserModal from '../../../components/InviteUserModal'

interface OrgUser {
  access_id: number; id: number; name: string; email: string; phone: string | null
  avatar: string | null; last_login_at: string | null
  profile_code: string; profile_label: string
  status_code: string; status_label: string
  joined_at: string
}

interface Invite {
  id: number; name: string; email: string
  profile_code: string; profile_label: string
  status: string; is_used: boolean; is_expired: boolean
  invited_by: string | null; expires_at: string; created_at: string
}


export default function UsersPage() {
  const navigate = useNavigate()
  const [users, setUsers] = useState<OrgUser[]>([])
  const [invites, setInvites] = useState<Invite[]>([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<'users' | 'invites'>('users')
  const [inviteModalOpen, setInviteModalOpen] = useState(false)

  useEffect(() => { fetchAll() }, [])

  async function fetchAll() {
    setLoading(true)
    try {
      const [usersRes, invitesRes] = await Promise.all([
        axios.get(buildApiUrl('/v1/organization-users')),
        axios.get(buildApiUrl('/v1/organization-users/invites')),
      ])
      setUsers(usersRes.data.data)
      setInvites(invitesRes.data.data)
    } catch { /* */ } finally { setLoading(false) }
  }

  async function resendInvite(id: number) {
    try {
      await axios.post(buildApiUrl(`/v1/organization-users/invites/${id}/resend`))
      toast('success', 'Convite reenviado')
      fetchAll()
    } catch { toast('error', 'Erro ao reenviar') }
  }

  async function deleteInvite(id: number) {
    try {
      await axios.delete(buildApiUrl(`/v1/organization-users/invites/${id}`))
      toast('success', 'Convite cancelado')
      fetchAll()
    } catch { toast('error', 'Erro ao cancelar') }
  }


  const pendingInvites = invites.filter(i => i.status === 'PENDING')

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/settings/general')} className="h-9 w-9 flex items-center justify-center rounded-lg hover:bg-gray-200/60 text-gray-400 hover:text-gray-600 transition-colors">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Usuários</h1>
            <p className="text-gray-500 mt-1">Gerencie usuários e convites da organização</p>
          </div>
        </div>
        <Button onClick={() => setInviteModalOpen(true)}>
          <Plus className="w-4 h-4 mr-1.5" />Convidar
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-6 border-b border-gray-200">
        <button onClick={() => setActiveTab('users')} className={clsx('pb-2.5 text-sm font-medium transition-colors border-b-2 -mb-px', activeTab === 'users' ? 'text-brand-600 border-brand-500' : 'text-gray-500 border-transparent hover:text-gray-700')}>
          Usuários ({users.length})
        </button>
        <button onClick={() => setActiveTab('invites')} className={clsx('pb-2.5 text-sm font-medium transition-colors border-b-2 -mb-px', activeTab === 'invites' ? 'text-brand-600 border-brand-500' : 'text-gray-500 border-transparent hover:text-gray-700')}>
          Convites {pendingInvites.length > 0 && <span className="ml-1 bg-brand-100 text-brand-600 text-xs px-1.5 py-0.5 rounded-full">{pendingInvites.length}</span>}
        </button>
      </div>

      {/* Usuários */}
      {activeTab === 'users' && (
        <Card padding="none">
          {loading ? (
            <div className="space-y-3 p-6">{[1,2,3].map(i => <div key={i} className="h-14 bg-gray-100 rounded-xl animate-pulse" />)}</div>
          ) : users.length === 0 ? (
            <div className="p-6">
              <EmptyState icon={<Users className="w-7 h-7" />} title="Nenhum usuário" description="Convide pessoas para acessar a organização." />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usuário</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Perfil</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Último acesso</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user.access_id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-brand-100 text-brand-600 flex items-center justify-center text-sm font-medium">
                            {user.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">{user.name}</p>
                            <p className="text-xs text-gray-500">{user.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3.5">
                        <span className="text-sm text-gray-700">{user.profile_label}</span>
                      </td>
                      <td className="px-4 py-3.5">
                        <Badge variant={user.status_code === 'ACTIVE' ? 'success' : 'gray'} size="sm" dot>{user.status_label}</Badge>
                      </td>
                      <td className="px-4 py-3.5 text-sm text-gray-500">
                        {user.last_login_at ? formatRelativeDate(user.last_login_at) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Convites */}
      {activeTab === 'invites' && (
        <Card padding="none">
          {loading ? (
            <div className="space-y-3 p-6">{[1,2,3].map(i => <div key={i} className="h-14 bg-gray-100 rounded-xl animate-pulse" />)}</div>
          ) : invites.length === 0 ? (
            <div className="p-6">
              <EmptyState
                icon={<Mail className="w-7 h-7" />}
                title="Nenhum convite"
                description="Envie convites para adicionar pessoas à organização."
                action={<Button size="sm" onClick={() => setInviteModalOpen(true)}><Plus className="w-4 h-4 mr-1" />Convidar</Button>}
              />
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {invites.map(invite => (
                <div key={invite.id} className="flex items-center justify-between px-4 py-3.5 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className={clsx('w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium',
                      invite.status === 'PENDING' ? 'bg-warning-100 text-warning-600' :
                      invite.status === 'USED' ? 'bg-success-100 text-success-600' : 'bg-gray-100 text-gray-400'
                    )}>
                      <Mail className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{invite.name}</p>
                      <p className="text-xs text-gray-500">{invite.email} &middot; {invite.profile_label}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <Badge variant={invite.status === 'PENDING' ? 'warning' : invite.status === 'USED' ? 'success' : 'gray'} size="sm">
                        {invite.status === 'PENDING' ? 'Pendente' : invite.status === 'USED' ? 'Aceito' : 'Expirado'}
                      </Badge>
                      <p className="text-[11px] text-gray-400 mt-0.5">{formatRelativeDate(invite.created_at)}</p>
                    </div>
                    {invite.status === 'PENDING' && (
                      <div className="flex gap-1">
                        <button onClick={() => resendInvite(invite.id)} className="h-8 w-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-brand-500 hover:bg-brand-50 transition-colors" title="Reenviar">
                          <RefreshCw className="w-4 h-4" />
                        </button>
                        <button onClick={() => deleteInvite(invite.id)} className="h-8 w-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-error-500 hover:bg-error-50 transition-colors" title="Cancelar">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                    {invite.status === 'EXPIRED' && (
                      <button onClick={() => resendInvite(invite.id)} className="h-8 w-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-brand-500 hover:bg-brand-50 transition-colors" title="Reenviar">
                        <RefreshCw className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Modal convidar */}
      <InviteUserModal
        open={inviteModalOpen}
        onClose={() => setInviteModalOpen(false)}
        onSuccess={() => { setActiveTab('invites'); fetchAll() }}
      />
    </div>
  )
}
